import type {
  AdPlacement,
  AdService,
  AdStatusEvent,
  ConsentStatus,
  InterstitialAdResult,
  RewardedAdResult
} from "./ads";

export type CapacitorAdMobMode = "test" | "production";

export interface CapacitorAdMobAdConfig {
  readonly mode?: CapacitorAdMobMode;
  readonly rewardedHintAdUnitId?: string;
  readonly interstitialLevelTransitionAdUnitId?: string;
  readonly rewardedHintAlias?: string;
  readonly interstitialLevelTransitionAlias?: string;
}

export interface CapacitorAdMobServiceOptions<
  TInitializeOptions = CapacitorAdMobInitializeOptions,
  TConsentRequestOptions = CapacitorAdMobConsentRequestOptions
> {
  readonly plugin: CapacitorAdMobPluginLike<TInitializeOptions, TConsentRequestOptions>;
  readonly config?: CapacitorAdMobAdConfig;
  readonly initializeOptions?: TInitializeOptions;
  readonly consentRequestOptions?: TConsentRequestOptions;
}

export interface CapacitorAdMobPluginLike<
  TInitializeOptions = CapacitorAdMobInitializeOptions,
  TConsentRequestOptions = CapacitorAdMobConsentRequestOptions
> {
  initialize(options?: TInitializeOptions): Promise<void>;
  requestConsentInfo(options?: TConsentRequestOptions): Promise<CapacitorAdMobConsentInfoLike>;
  showConsentForm(): Promise<CapacitorAdMobConsentInfoLike>;
  showPrivacyOptionsForm(): Promise<unknown>;
  prepareRewardVideoAd(options: CapacitorAdMobAdOptions): Promise<unknown>;
  showRewardVideoAd(): Promise<CapacitorAdMobRewardItemLike>;
  prepareInterstitial(options: CapacitorAdMobAdOptions): Promise<unknown>;
  showInterstitial(): Promise<void>;
}

export interface CapacitorAdMobInitializeOptions {
  readonly testingDevices?: string[];
  readonly initializeForTesting?: boolean;
  readonly tagForChildDirectedTreatment?: boolean;
  readonly tagForUnderAgeOfConsent?: boolean;
}

export interface CapacitorAdMobConsentRequestOptions {
  readonly debugGeography?: string;
  readonly testDeviceIdentifiers?: string[];
  readonly tagForUnderAgeOfConsent?: boolean;
}

export interface CapacitorAdMobConsentInfoLike {
  readonly status?: unknown;
  readonly canRequestAds?: boolean;
  readonly isConsentFormAvailable?: boolean;
}

export interface CapacitorAdMobAdOptions {
  readonly adId: string;
  readonly isTesting?: boolean;
  readonly npa?: boolean;
  readonly immersiveMode?: boolean;
}

export interface CapacitorAdMobRewardItemLike {
  readonly type?: unknown;
  readonly amount?: unknown;
}

interface ResolvedCapacitorAdMobConfig {
  readonly mode: CapacitorAdMobMode;
  readonly rewardedHintAdUnitId: string;
  readonly interstitialLevelTransitionAdUnitId: string;
  readonly rewardedHintAlias: string;
  readonly interstitialLevelTransitionAlias: string;
}

const GOOGLE_TEST_ADMOB_CONFIG: ResolvedCapacitorAdMobConfig = {
  mode: "test",
  rewardedHintAdUnitId: "ca-app-pub-3940256099942544/5224354917",
  interstitialLevelTransitionAdUnitId: "ca-app-pub-3940256099942544/1033173712",
  rewardedHintAlias: "android_rewarded_hint_test",
  interstitialLevelTransitionAlias: "android_interstitial_level_transition_test"
};

export function createCapacitorAdMobService<
  TInitializeOptions = CapacitorAdMobInitializeOptions,
  TConsentRequestOptions = CapacitorAdMobConsentRequestOptions
>(options: CapacitorAdMobServiceOptions<TInitializeOptions, TConsentRequestOptions>): AdService {
  return new CapacitorAdMobService(options);
}

export function isCapacitorAdMobPluginAvailable(
  plugin: unknown
): plugin is CapacitorAdMobPluginLike<unknown, unknown> {
  if (plugin === null || typeof plugin !== "object") {
    return false;
  }
  const candidate = plugin as Partial<Record<keyof CapacitorAdMobPluginLike, unknown>>;
  return (
    typeof candidate.initialize === "function" &&
    typeof candidate.requestConsentInfo === "function" &&
    typeof candidate.showPrivacyOptionsForm === "function" &&
    typeof candidate.prepareRewardVideoAd === "function" &&
    typeof candidate.showRewardVideoAd === "function" &&
    typeof candidate.prepareInterstitial === "function" &&
    typeof candidate.showInterstitial === "function"
  );
}

class CapacitorAdMobService<TInitializeOptions, TConsentRequestOptions> implements AdService {
  private readonly listeners = new Set<(event: AdStatusEvent) => void>();
  private readonly plugin: CapacitorAdMobPluginLike<TInitializeOptions, TConsentRequestOptions>;
  private readonly config: ResolvedCapacitorAdMobConfig;
  private readonly initializeOptions: TInitializeOptions | undefined;
  private readonly consentRequestOptions: TConsentRequestOptions | undefined;
  private initializePromise: Promise<void> | null = null;
  private consentStatus: ConsentStatus = "unknown";
  private canRequestAds = false;

  public constructor(
    options: CapacitorAdMobServiceOptions<TInitializeOptions, TConsentRequestOptions>
  ) {
    this.plugin = options.plugin;
    this.config = resolveCapacitorAdMobConfig(options.config);
    this.initializeOptions = resolveInitializeOptions(this.config.mode, options.initializeOptions);
    this.consentRequestOptions = options.consentRequestOptions;
  }

  public canShowRewardedHint(): boolean {
    return this.canRequestAds;
  }

  public async showRewardedHint(): Promise<RewardedAdResult> {
    try {
      const consent = await this.updateConsentBeforeAd("rewarded-hint");
      if (!canShowAdsForConsent(consent, this.canRequestAds)) {
        return this.failRewardedHint("CONSENT_REQUIRED");
      }

      await this.plugin.prepareRewardVideoAd(this.createAdOptions("rewarded-hint"));
      this.emit({
        placement: "rewarded-hint",
        status: "ready",
        adUnitAlias: this.config.rewardedHintAlias
      });
      const reward = await this.plugin.showRewardVideoAd();
      this.emit({
        placement: "rewarded-hint",
        status: "showing",
        adUnitAlias: this.config.rewardedHintAlias
      });
      this.emit({
        placement: "rewarded-hint",
        status: "closed",
        adUnitAlias: this.config.rewardedHintAlias
      });
      return {
        placement: "rewarded-hint",
        completed: true,
        rewardGranted: reward !== null && typeof reward === "object"
      };
    } catch (error: unknown) {
      return this.failRewardedHint(resolvePublicErrorCode(error));
    }
  }

  public canShowInterstitial(): boolean {
    return this.canRequestAds;
  }

  public async showInterstitial(): Promise<InterstitialAdResult> {
    try {
      const consent = await this.updateConsentBeforeAd("level-transition");
      if (!canShowAdsForConsent(consent, this.canRequestAds)) {
        return this.failInterstitial("CONSENT_REQUIRED");
      }

      await this.plugin.prepareInterstitial(this.createAdOptions("level-transition"));
      this.emit({
        placement: "level-transition",
        status: "ready",
        adUnitAlias: this.config.interstitialLevelTransitionAlias
      });
      await this.plugin.showInterstitial();
      this.emit({
        placement: "level-transition",
        status: "showing",
        adUnitAlias: this.config.interstitialLevelTransitionAlias
      });
      this.emit({
        placement: "level-transition",
        status: "closed",
        adUnitAlias: this.config.interstitialLevelTransitionAlias
      });
      return {
        placement: "level-transition",
        completed: true
      };
    } catch (error: unknown) {
      return this.failInterstitial(resolvePublicErrorCode(error));
    }
  }

  public async requestConsent(): Promise<ConsentStatus> {
    await this.initializeOnce();
    return this.updateConsent("level-transition");
  }

  public isConsentRequired(): boolean {
    return this.consentStatus === "required";
  }

  public async showPrivacyOptionsForm(): Promise<ConsentStatus> {
    await this.initializeOnce();
    const result = await this.plugin.showPrivacyOptionsForm();
    if (result && typeof result === "object") {
      this.applyConsentInfo(result);
    }
    return this.consentStatus;
  }

  public onStatusChange(listener: (event: AdStatusEvent) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private initializeOnce(): Promise<void> {
    if (this.initializePromise === null) {
      this.initializePromise = this.plugin.initialize(this.initializeOptions);
    }
    return this.initializePromise;
  }

  private async updateConsentBeforeAd(placement: AdPlacement): Promise<ConsentStatus> {
    await this.initializeOnce();
    return this.updateConsent(placement);
  }

  private async updateConsent(placement: AdPlacement): Promise<ConsentStatus> {
    this.emit({
      placement,
      status: "consent-requested",
      adUnitAlias: this.resolveAlias(placement)
    });
    const info = await this.plugin.requestConsentInfo(this.consentRequestOptions);
    const status = this.applyConsentInfo(info);
    if (status === "required" && info.isConsentFormAvailable === true) {
      return this.applyReadyConsentInfo(placement, await this.plugin.showConsentForm());
    }
    return this.applyReadyConsentStatus(placement, status);
  }

  private applyReadyConsentInfo(
    placement: AdPlacement,
    info: CapacitorAdMobConsentInfoLike
  ): ConsentStatus {
    return this.applyReadyConsentStatus(placement, this.applyConsentInfo(info));
  }

  private applyReadyConsentStatus(placement: AdPlacement, status: ConsentStatus): ConsentStatus {
    if (canShowAdsForConsent(status, this.canRequestAds)) {
      this.emit({ placement, status: "ready", adUnitAlias: this.resolveAlias(placement) });
    }
    return status;
  }

  private applyConsentInfo(info: CapacitorAdMobConsentInfoLike): ConsentStatus {
    this.consentStatus = normalizeConsentStatus(info.status);
    this.canRequestAds = info.canRequestAds ?? this.consentStatus !== "required";
    return this.consentStatus;
  }

  private createAdOptions(placement: AdPlacement): CapacitorAdMobAdOptions {
    return {
      adId:
        placement === "rewarded-hint"
          ? this.config.rewardedHintAdUnitId
          : this.config.interstitialLevelTransitionAdUnitId,
      isTesting: this.config.mode === "test",
      immersiveMode: true
    };
  }

  private failRewardedHint(errorCode: string): RewardedAdResult {
    this.emit({
      placement: "rewarded-hint",
      status: "failed",
      adUnitAlias: this.config.rewardedHintAlias,
      errorCode
    });
    return {
      placement: "rewarded-hint",
      completed: false,
      rewardGranted: false,
      errorCode
    };
  }

  private failInterstitial(errorCode: string): InterstitialAdResult {
    this.emit({
      placement: "level-transition",
      status: "failed",
      adUnitAlias: this.config.interstitialLevelTransitionAlias,
      errorCode
    });
    return {
      placement: "level-transition",
      completed: false,
      errorCode
    };
  }

  private resolveAlias(placement: AdPlacement): string {
    return placement === "rewarded-hint"
      ? this.config.rewardedHintAlias
      : this.config.interstitialLevelTransitionAlias;
  }

  private emit(event: AdStatusEvent): void {
    for (const listener of this.listeners) {
      listener(event);
    }
  }
}

function resolveCapacitorAdMobConfig(
  config: CapacitorAdMobAdConfig | undefined
): ResolvedCapacitorAdMobConfig {
  if (config?.mode !== "production") {
    return GOOGLE_TEST_ADMOB_CONFIG;
  }

  if (
    !config.rewardedHintAdUnitId ||
    !config.interstitialLevelTransitionAdUnitId ||
    !config.rewardedHintAlias ||
    !config.interstitialLevelTransitionAlias
  ) {
    throw new Error("Production AdMob config is missing required ad unit aliases or IDs.");
  }

  return {
    mode: "production",
    rewardedHintAdUnitId: config.rewardedHintAdUnitId,
    interstitialLevelTransitionAdUnitId: config.interstitialLevelTransitionAdUnitId,
    rewardedHintAlias: config.rewardedHintAlias,
    interstitialLevelTransitionAlias: config.interstitialLevelTransitionAlias
  };
}

function resolveInitializeOptions<TInitializeOptions>(
  mode: CapacitorAdMobMode,
  options: TInitializeOptions | undefined
): TInitializeOptions | undefined {
  if (mode !== "test") {
    return options;
  }
  return {
    initializeForTesting: true,
    ...(options && typeof options === "object" ? options : {})
  } as TInitializeOptions;
}

function normalizeConsentStatus(status: unknown): ConsentStatus {
  switch (status) {
    case "NOT_REQUIRED":
    case "not-required":
      return "not-required";
    case "OBTAINED":
    case "obtained":
      return "obtained";
    case "REQUIRED":
    case "required":
      return "required";
    case "UNKNOWN":
    case "unknown":
    default:
      return "unknown";
  }
}

function canShowAdsForConsent(status: ConsentStatus, canRequestAds: boolean): boolean {
  return canRequestAds && (status === "not-required" || status === "obtained");
}

function resolvePublicErrorCode(error: unknown): string {
  if (error && typeof error === "object") {
    const code = "code" in error ? (error as { readonly code?: unknown }).code : undefined;
    if (typeof code === "string" && code.trim() !== "") {
      return code;
    }
    if (typeof code === "number") {
      return `ADMOB_${code}`;
    }
  }
  return "ADMOB_ERROR";
}
